import { useState } from "react";
import Button from "../../../shared/components/Button";
import { Dropdown, TextField } from "../../../shared/components/form";
import {
  createNode,
  definitions,
  type Edge,
  type GroupGraph,
  type NodeKind,
  type WorkflowNode,
} from "../model";
import "./GroupEditor.scss";

type GroupEditorProps = {
  group: GroupGraph;
  onChange: (group: GroupGraph) => void;
};

type Endpoint = {
  id: string;
  side: "input" | "output";
  portID?: string;
};

function ports(prefix: "group-input" | "group-output", count: number) {
  return Array.from({ length: count }, (_, index) => ({
    id: `${prefix}-${index + 1}`,
    label: `${prefix === "group-input" ? "Input" : "Output"} ${index + 1}`,
  }));
}

function childInputPorts(node: WorkflowNode) {
  return node.input === "None" ? [] : (node.inputPorts ?? ["input"]);
}

function childOutputPort(node: WorkflowNode) {
  return node.kind === "split" ? "branch-1" : "output";
}

export function GroupEditor({ group, onChange }: GroupEditorProps) {
  const childDefinitions = definitions.filter(
    (definition) => definition.kind !== "group",
  );
  const [kind, setKind] = useState<NodeKind>("sampler");
  const [connecting, setConnecting] = useState<Endpoint | null>(null);

  const update = (changes: Partial<GroupGraph>) => {
    onChange({ ...group, ...changes });
  };

  const resizePorts = (type: "inputs" | "outputs", value: string) => {
    const count = Math.min(8, Math.max(1, Number(value) || 1));
    const nextPorts = ports(
      type === "inputs" ? "group-input" : "group-output",
      count,
    );
    const validIDs = new Set([
      ...nextPorts.map((port) => port.id),
      ...(type === "inputs"
        ? group.outputs.map((port) => port.id)
        : group.inputs.map((port) => port.id)),
      ...group.nodes.map((node) => node.id),
    ]);
    const nextEdges = group.edges.filter(
      (edge) => validIDs.has(edge.source) && validIDs.has(edge.target),
    );
    update({ [type]: nextPorts, edges: nextEdges });
  };

  const addNode = () => {
    const node = createNode(
      kind,
      `group-node-${Date.now()}`,
      150,
      30 + (group.nodes.length % 3) * 82,
    );
    update({ nodes: [...group.nodes, node] });
  };

  const removeNode = (nodeID: string) => {
    update({
      nodes: group.nodes.filter((node) => node.id !== nodeID),
      edges: group.edges.filter(
        (edge) => edge.source !== nodeID && edge.target !== nodeID,
      ),
    });
    setConnecting(null);
  };

  const connect = (endpoint: Endpoint) => {
    if (!connecting) {
      if (endpoint.side === "output") setConnecting(endpoint);
      return;
    }
    if (connecting.side === "output" && endpoint.side === "input") {
      const nextEdge: Edge = {
        source: connecting.id,
        sourcePort: connecting.portID,
        target: endpoint.id,
        targetPort: endpoint.portID,
      };
      const duplicate = group.edges.some(
        (edge) =>
          edge.source === nextEdge.source &&
          edge.sourcePort === nextEdge.sourcePort &&
          edge.target === nextEdge.target &&
          edge.targetPort === nextEdge.targetPort,
      );
      if (!duplicate) update({ edges: [...group.edges, nextEdge] });
    }
    setConnecting(null);
  };

  const endpointLabel = (id: string) => {
    const port = [...group.inputs, ...group.outputs].find(
      (candidate) => candidate.id === id,
    );
    if (port) return port.label;
    return group.nodes.find((node) => node.id === id)?.title ?? id;
  };

  return (
    <div className="group-editor">
      <div className="group-toolbar">
        <div>
          <p className="group-kicker">Nested workflow</p>
          <h4>Group canvas</h4>
        </div>
        <p className="group-help">
          Connect ports to compose this group as one reusable node.
        </p>
      </div>
      <div className="group-controls">
        <label>
          <span>Inputs</span>
          <TextField
            max="8"
            min="1"
            type="number"
            value={group.inputs.length}
            onChange={(event) => resizePorts("inputs", event.target.value)}
          />
        </label>
        <label>
          <span>Outputs</span>
          <TextField
            max="8"
            min="1"
            type="number"
            value={group.outputs.length}
            onChange={(event) => resizePorts("outputs", event.target.value)}
          />
        </label>
        <label className="add-child">
          <span>Add node</span>
          <div>
            <Dropdown value={kind}
            onChange={(event) => setKind(event.target.value as NodeKind)}>{childDefinitions.map((definition) => (
              <option key={definition.kind} value={definition.kind}>
                {definition.label}
              </option>
            ))}</Dropdown>
            <Button onClick={addNode} type="button" variant="primary">
              Add
            </Button>
          </div>
        </label>
      </div>
      <div className="group-canvas">
        <div className="group-ports group-ports--inputs">
          {group.inputs.map((port) => (
            <div className="group-port" key={port.id}>
              <span>{port.label}</span>
              <Button
                aria-label={`Connect ${port.label}`}
                className={`group-port-handle group-port-handle--output ${connecting?.id === port.id ? "active" : ""}`}
                onClick={() => connect({ id: port.id, side: "output" })}
                type="button"
                variant="ghost"
              />
            </div>
          ))}
        </div>
        <div className="group-child-canvas">
          {group.nodes.length === 0 && (
            <p className="group-empty">Add nodes to build group workflow.</p>
          )}
          {group.nodes.map((node) => (
            <article
              className="group-child-node"
              key={node.id}
              style={{ left: node.x, top: node.y }}
            >
              <div className="group-child-header">
                <span>{node.title}</span>
                <Button
                  aria-label={`Remove ${node.title}`}
                  onClick={() => removeNode(node.id)}
                  type="button"
                  variant="ghost"
                >
                  ×
                </Button>
              </div>
              <div className="group-child-body">
                <div className="group-child-inputs">
                  {childInputPorts(node).map((portID) => (
                    <Button
                      className="group-child-handle group-child-handle--input"
                      key={portID}
                      onClick={() =>
                        connect({ id: node.id, side: "input", portID })
                      }
                      type="button"
                      variant="ghost"
                    >
                      {portID}
                    </Button>
                  ))}
                </div>
                <span className="group-child-detail">{node.subtitle}</span>
                <Button
                  className={`group-child-handle group-child-handle--output ${connecting?.id === node.id ? "active" : ""}`}
                  onClick={() =>
                    connect({
                      id: node.id,
                      side: "output",
                      portID: childOutputPort(node),
                    })
                  }
                  type="button"
                  variant="ghost"
                >
                  Output
                </Button>
              </div>
            </article>
          ))}
        </div>
        <div className="group-ports group-ports--outputs">
          {group.outputs.map((port) => (
            <div className="group-port" key={port.id}>
              <Button
                aria-label={`Connect ${port.label}`}
                className="group-port-handle group-port-handle--input"
                onClick={() => connect({ id: port.id, side: "input" })}
                type="button"
                variant="ghost"
              />
              <span>{port.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="group-connections">
        <span className="group-connections-title">Connections</span>
        {group.edges.length === 0 ? (
          <span className="group-connections-empty">None yet</span>
        ) : (
          group.edges.map((edge, index) => (
            <span
              className="group-connection"
              key={`${edge.source}-${edge.target}-${index}`}
            >
              {endpointLabel(edge.source)} → {endpointLabel(edge.target)}
            </span>
          ))
        )}
      </div>
    </div>
  );
}
